import { useEffect, useMemo, useRef, useState } from "react"
import { Meeting, TranscriptEntry } from "@/schema"
import EmptyState from "@ui/empty-state"
import HighlightedText from "@ui/highlighted-text"
import { Button, Input, ScrollArea, Skeleton, Tooltip, TooltipContent, TooltipTrigger } from "@ui/shad"
import ZeroState from "@ui/zero-state"
import { Check, Clock, Copy, X } from "lucide-react"
import { useCopyToClipboard } from "react-use"

import { useDebounce } from "@/hooks/use-debounce"
import { useScrollAreaHeight } from "@/hooks/use-scroll-area-height"

export default function TranscriptList({
  meeting,
  handleSeekTo,
  currentTime,
  isLoading,
  isVideoCollapsed,
}: {
  meeting?: Meeting
  handleSeekTo: (time: number) => void
  currentTime: number
  isLoading: boolean
  isVideoCollapsed: boolean
}) {
  const [search, setSearch] = useState("")
  const [searchText, setSearchText] = useState("")
  const [isManualScrolling, setIsManualScrolling] = useState(false)
  const [, copyToClipboard] = useCopyToClipboard()
  const [copied, setCopied] = useState(false)
  const transcript: TranscriptEntry[] = Array.isArray(meeting?.transcript) ? meeting.transcript : []

  const speakerNameMap = useMemo(() => {
    const map = new Map<string | number, string>()
    meeting?.transcript_speakers?.forEach((s) => map.set(String(s.provider_id), s.effective_name))
    return map
  }, [meeting?.transcript_speakers])
  const { tableTop, containerRef } = useScrollAreaHeight()
  const currentEntryRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout>(undefined)

  const debouncedSearch = useDebounce((value: string) => {
    setSearchText(value.trim())
  }, 500)

  const filteredTranscript = transcript.filter((entry) => {
    if (!searchText) return true
    const text = entry.words.map((word) => word.text).join(" ")
    return text.toLowerCase().includes(searchText.toLowerCase())
  })

  const formatRelativeTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  const isCurrentEntry = (entry: TranscriptEntry) => {
    const entryTime = entry.words[0].start_timestamp.relative * 1000
    const nextEntry = transcript[transcript.indexOf(entry) + 1]
    const nextEntryTime = nextEntry ? nextEntry.words[0].start_timestamp.relative * 1000 : Infinity
    return currentTime >= entryTime && currentTime < nextEntryTime
  }

  useEffect(() => {
    if (currentEntryRef.current && scrollAreaRef.current && !isManualScrolling) {
      const entry = currentEntryRef.current
      const viewport = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]")

      if (viewport) {
        const viewportRect = viewport.getBoundingClientRect()
        const entryRect = entry.getBoundingClientRect()

        const scrollTop = entry.offsetTop - viewportRect.height / 2 + entryRect.height / 2

        viewport.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        })
      }
    }
  }, [currentTime, isManualScrolling])

  function handleScroll() {
    setIsManualScrolling(true)

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsManualScrolling(false)
    }, 3000)
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value)
    debouncedSearch(e.target.value)
  }

  function handleClearSearch() {
    setSearch("")
    setSearchText("")
  }

  function handleCopyTranscript() {
    const text = meeting?.transcript_plaintext ?? meeting?.user_uploaded_transcript
    if (!text) return
    copyToClipboard(text)
    setCopied(true)
  }

  function handleRowClick(entry: TranscriptEntry) {
    handleSeekTo(entry.words[0].start_timestamp.relative * 1000)
    handleClearSearch()
  }

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  useEffect(() => {
    if (containerRef.current) {
      window.dispatchEvent(new Event("resize"))
    }
  }, [filteredTranscript.length, containerRef, isVideoCollapsed])

  if (isLoading)
    return (
      <div className="flex flex-col gap-4 p-4">
        {Array.from({ length: 20 }).map((_, index) => (
          <div key={index} className="flex items-center justify-between gap-6">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-3 w-36" />
              <Skeleton className="h-4 w-full" />
            </div>
            <Skeleton className="h-3 w-10" />
          </div>
        ))}
      </div>
    )

  if (meeting?.transcript_provider === "user_upload" && meeting?.user_uploaded_transcript) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-end border-b px-4 py-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="iconSm" onClick={handleCopyTranscript}>
                {copied ? <Check className="text-green-accent" /> : <Copy className="text-icon" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{copied ? "Copied" : "Copy transcript"}</TooltipContent>
          </Tooltip>
        </div>
        <ScrollArea style={{ height: `calc(100vh - ${tableTop}px - 100px)` }}>
          <div className="p-4 text-sm whitespace-pre-wrap">{meeting.user_uploaded_transcript}</div>
        </ScrollArea>
      </div>
    )
  }

  if (!transcript || transcript.length === 0) {
    const isPurged = Boolean(meeting?.transcript_purged)
    return (
      <div className="flex h-full items-center justify-center p-24">
        <EmptyState
          title={isPurged ? "Transcript not available" : "No transcript found"}
          description={
            isPurged
              ? "The transcript for this meeting has been removed according to your retention settings."
              : "The transcript for this meeting is not available."
          }
        />
      </div>
    )
  }

  function renderTranscript() {
    if (search.length > 0 && filteredTranscript.length === 0) {
      return (
        <ZeroState
          title="No results found"
          description="No transcript entries match your search. Please try again."
          onResetClick={handleClearSearch}
          resetText="Clear search"
        />
      )
    }

    return (
      <>
        {filteredTranscript.map((entry, index) => (
          <div
            key={entry.words[0].start_timestamp.relative + index}
            ref={isCurrentEntry(entry) ? currentEntryRef : null}
            className={`hover:bg-muted/50 active:bg-muted flex cursor-pointer items-start justify-between gap-4 rounded-lg p-2 py-1 transition-colors ${
              isCurrentEntry(entry) ? "bg-primary/5" : ""
            }`}
            onClick={() => handleRowClick(entry)}
          >
            <div className="flex flex-col">
              <p className="text-muted-foreground text-xs">
                {speakerNameMap.get(String(entry.participant.id)) || entry.participant.name || "Unknown Speaker"}
              </p>
              <p className="text-sm">
                <HighlightedText text={entry.words.map((word) => word.text).join(" ")} searchQuery={searchText} />
              </p>
            </div>
            <div className="flex w-16 items-center justify-end gap-1">
              <Clock className="text-icon h-3 w-3" />
              <p className="text-muted-foreground w-8 text-left text-xs">
                {formatRelativeTime(entry.words[0].start_timestamp.relative)}
              </p>
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-2">
        <div className="relative w-fit">
          <Input placeholder="Search transcript" value={search} onChange={handleSearchChange} className="w-72 pr-8" />
          {search && (
            <Button
              variant="ghost"
              size="iconXs"
              disableInteraction
              className="absolute top-1/2 right-0.5 mr-1 -translate-y-1/2"
              onClick={handleClearSearch}
            >
              <X className="text-icon h-4 w-4" />
            </Button>
          )}
        </div>
        {meeting?.transcript_plaintext && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="iconSm" onClick={handleCopyTranscript}>
                {copied ? <Check className="text-green-accent" /> : <Copy className="text-icon" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{copied ? "Copied" : "Copy transcript"}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div ref={containerRef} className="flex-1">
        <ScrollArea
          ref={scrollAreaRef}
          style={{ height: `calc(100vh - ${tableTop}px)` }}
          onScrollCapture={handleScroll}
        >
          <div className="flex flex-col gap-1 p-2 pb-6">{renderTranscript()}</div>
        </ScrollArea>
      </div>
    </div>
  )
}
